import { generateUUID, Result, TransportAbstractionBuilder, TransportContext } from "@peercolab/engine";
import {
  CreateLesson, Lesson, UpdateLessonDescription, UpdateLessonDuration, UpdateLessonLongDescription, UpdateLessonName, UpdateLessonThumbnailUrl, UpdateLessonVideoUrl,
  CreateMaterialRequirement, UpdateMaterialRequirement, DeleteMaterialRequirement,
  CreateMaterialStep, UpdateMaterialStep, DeleteMaterialStep,
  CreateMaterialQuestion, UpdateMaterialQuestion, DeleteMaterialQuestion,
  AddCommand, RemoveCommand,
  UpdateLessonMinimumNumberOfCorrectAnswers,
  MoveMaterialRequirements,
  MoveMaterialSteps,
  MoveMaterialQuestions,
} from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/Lessons/PathItems";
import { Course, CreateCourse, DeleteCourse, GetCourse, Module, SearchCourses, UpdateCourseBreedIds, UpdateCourseCategories, UpdateCourseDescription, UpdateCourseDuration, UpdateCourseLongDescription, UpdateCourseName, UpdateCoursePaymentProductId, UpdateCourseSequentialCompletion, UpdateCourseSpeciesId, UpdateCourseState, UpdateCourseThumbnailUrl, UpdateCourseTrainerId, UpdateCourseType, UpdateCourseVideoUrl } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems";
import { AddModule, RemoveModule, UpdateModuleName, UpdateModuleDescription } from "../../PetKey/Main/Model/1_0_Initial/Backoffice/Training/Courses/PathItems";
import { SearchCoursesOutput } from "../../PetKey/Main/Model/1_0_Initial/PathItems";
import { applyStateTransition, handleInPlaceMoveSortOfIdElement, transitionState } from "../../../utils/GeneralHelpers";

export function mockCourseInterceptors(builder: TransportAbstractionBuilder) {
  const dbCourses: Course[] = []
  const dbCoursesPublished: Course[] = []

  type CourseAndModule = {
    course: Course,
    module: Module,
  }

type CourseModuleAndLesson = {
    course: Course,
    module: Module,
    lesson: Lesson,
  }

  const getCourse = (ctx: string | TransportContext): Result<Course> => {
    const id = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('courseId');
    const course = dbCourses.find(c => c.id === id);
    if (!course) {
      return Result.notFound('Backoffice.Course.NotFound', `Course with id ${id} not found`);
    }
    return Result.ok(course);
  }

  const getModule = (ctx: string | TransportContext, moduleId: string): Result<CourseAndModule> => {
    const courseId = typeof ctx === 'string' ? ctx : ctx.getPathParameter<string>('courseId');
    const extractedModuleId = moduleId;
    const course = dbCourses.find(c => c.id === courseId);
    if (!course) {
      return Result.notFound('Backoffice.Course.NotFound', `Course with id ${courseId} not found`);
    }
    const module = course.modules.find(m => m.module.id === extractedModuleId);
    if (!module) {
      return Result.notFound('Backoffice.Course.Modules.NotFound', `Module with id ${extractedModuleId} not found in course ${courseId}`);
    }
    return Result.ok({ course, module: module.module });
  }

  const getLesson = (ctx: TransportContext): Result<CourseModuleAndLesson> => {
    const courseId = ctx.getPathParameter<string>('courseId');
    const lessonId = ctx.getPathParameter<string>('lessonId');
    const course = dbCourses.find(c => c.id === courseId);
    if (!course) {
      return Result.notFound('Backoffice.Course.NotFound', `Course with id ${courseId} not found`);
    }
    const module = course.modules.find(m => m.module.lessons.some(l => l.lesson.id === lessonId));
    if (!module) {
      return Result.notFound('Backoffice.Course.Lesson.NotFound', `Lesson with id ${lessonId} not found in course ${courseId}`);
    }
    const lesson = module.module.lessons.find(l => l.lesson.id === lessonId)?.lesson as Lesson;
    return Result.ok({ course, module: module.module, lesson });
  }

  const saveCourse = (result: Result<Course>, ctx: TransportContext): Result<Course> => {
    if (!result.success) {
      return result;
    }
    const course = result.value;
    const existingIndex = dbCourses.findIndex(c => c.id === course.id);
    if (existingIndex !== -1) {
      const existingPublishedIndex = dbCoursesPublished.findIndex(c => c.id === course.id);
      dbCourses[existingIndex] = applyStateTransition(dbCoursesPublished, existingPublishedIndex, result.meta, course, course.state, (itm, newState) => {
        itm.state = newState;
        return itm;
      });
      return Result.ok(dbCourses[existingIndex]);
    } else {
      dbCourses.push(course);
      return Result.ok(course);
    }
  }

  return builder
    .intercept(new SearchCourses().handle(async (input, ctx) => {
      const filtered = dbCourses
        .slice(input.offset, input.offset + input.limit)
        .filter(c =>
          c.name.toLowerCase().includes(input.query.toLowerCase()) ||
          c.details.description.toLowerCase().includes(input.query.toLowerCase())
        )
        .map(c => {
          const item: SearchCoursesOutput = {
            courseId: c.id,
            name: c.name,
            description: c.details.description,
            lessonCount: c.modules.flatMap(m => m.module.lessons).length,
            courseCategoryIds: c.details.categories,
            state: c.state,
          }
          return item;
        });
      return Result.ok(filtered);
    }))
    .intercept(new GetCourse().handle(async (input, ctx) => {
      return getCourse(ctx)
    }))
    .intercept(new CreateCourse().handle(async (input, ctx) => {
      const newCourse: Course = {
        id: input.courseId ?? generateUUID(),
        name: input.name,
        type: {
          type: input.type,
          speciesId: input.speciesId,
          breedIds: input.breedIds
        },
        details: {
          categories: input.categories ?? [],
          trainerId: input.trainerId,
          description: input.description ?? '',
          longDescription: input.longDescription ?? '',
          duration: 0,
          sequentialCompletion: false,
        },
        media: {
          thumbnail: { url: input.thumbnailUrl ?? ''},
          video: { url: input.videoUrl ?? '' },
        },
        paymentReference: {
          productId: ''
        },
        modules: [],
        state: { state: 'draft', lastPublishedAt: undefined, isDirty: false }
      }
      return saveCourse(Result.ok(newCourse), ctx);
    }))
    .intercept(new DeleteCourse().handle(async (input, ctx) => {
      const id = ctx.getPathParameter<string>('courseId');
      const idx = dbCourses.findIndex(c => c.id === id);
      if (idx === -1) {
        return Result.notFound('Backoffice.Courses.NotFound', `Course with id ${id} not found`);
      }
      dbCourses.splice(idx, 1);
      return Result.ok(undefined);
    }))
    .intercept(new UpdateCourseName().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.name = input.name;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseType().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.type.type = input.type;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseSpeciesId().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.type.speciesId = input.speciesId;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseBreedIds().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.type.breedIds = input.breedIds;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseThumbnailUrl().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.media.thumbnail.url = input.url;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseVideoUrl().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.media.video.url = input.url;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseDuration().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.duration = input.duration;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseDescription().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.description = input.description;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseTrainerId().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.trainerId = input.trainerId;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseLongDescription().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.longDescription = input.longDescription;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseCategories().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.categories = input.categories;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseSequentialCompletion().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.details.sequentialCompletion = input.sequentialCompletion;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCoursePaymentProductId().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((value, meta) => {
          value.paymentReference.productId = input.productId;
          return Result.ok(value, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Module operations
    .intercept(new AddModule().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((course, meta) => {
          const newModule = {
            id: input.moduleId ?? generateUUID(),
            name: input.name,
            description: input.description ?? '',
            lessons: []
          };
          const sortId = course.modules.length;
          course.modules.push({ sortId, module: newModule });
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new RemoveModule().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((course, meta) => {
          const idx = course.modules.findIndex(m => m.module.id === input.moduleId);
          if (idx !== -1) {
            course.modules.splice(idx, 1);
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateModuleName().handle(async (input, ctx) => {
      return saveCourse(getModule(ctx, input.moduleId)
        .maybe(({course, module}, meta) => {
          module.name = input.name;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateModuleDescription().handle(async (input, ctx) => {
      return saveCourse(getModule(ctx, input.moduleId)
        .maybe(({course, module}, meta) => {
          module.description = input.description;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Lesson operations
    .intercept(new CreateLesson().handle(async (input, ctx) => {
      const newLessonId = input.lessonId ?? generateUUID();
      return saveCourse(getModule(ctx, input.moduleId)
        .maybe(({ course, module }, meta) => {
          const newLesson: Lesson = {
            id: newLessonId,
            name: input.name,
            media: {
              thumbnail: { url: input.thumbnailUrl ?? '' },
              video: { url: input.videoUrl ?? '' }
            },
            details: {
              description: input.description,
              longDescription: input.longDescription ?? '',
              duration: input.duration ?? 0,
            },
            material: {
              linkedCommands: [],
              minimumNumberOfCorrectAnswers: 0,
              questions: [],
              requirements: [],
              steps: [],
            },
          };
          module.lessons.push({ sortId: module.lessons.length, lesson: newLesson });
          return Result.ok(course, meta);
        }), ctx)
        .maybe((course) => Result.ok(course.modules.flatMap(m => m.module.lessons).find(l => l.lesson.id === newLessonId)?.lesson as Lesson));
    }))
    .intercept(new UpdateLessonName().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.name = input.name;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateLessonDescription().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.details.description = input.description;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateLessonLongDescription().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.details.longDescription = input.longDescription;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateLessonThumbnailUrl().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.media.thumbnail.url = input.url;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateLessonVideoUrl().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.media.video.url = input.url;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateLessonDuration().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.details.duration = input.duration;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))

    // Requirements
    .intercept(new CreateMaterialRequirement().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const newReq = {
            sortId: lesson.material.requirements.length,
            requirement: {
              id: generateUUID(),
              description: input.description
            }
          };
          lesson.material.requirements.push(newReq);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateMaterialRequirement().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const req = lesson.material.requirements.find(r => r.requirement.id === input.id);
          if (req) {
            req.requirement.description = input.description;
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new DeleteMaterialRequirement().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.requirements = lesson.material.requirements.filter(r => r.requirement.id !== input);
          // Re-sort sortId
          lesson.material.requirements.forEach((r, idx) => r.sortId = idx);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Steps
    .intercept(new CreateMaterialStep().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const newStep = {
            sortId: lesson.material.steps.length,
            step: {
              id: generateUUID(),
              name: input.name,
              description: input.description,
              thumbnail: input.thumbnail
            }
          };
          lesson.material.steps.push(newStep);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateMaterialStep().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const step = lesson.material.steps.find(s => s.step.id === input.id);
          if (step) {
            step.step.name = input.name;
            step.step.description = input.description;
            step.step.thumbnail = input.thumbnail;
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new DeleteMaterialStep().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.steps = lesson.material.steps.filter(s => s.step.id !== input);
          lesson.material.steps.forEach((s, idx) => s.sortId = idx);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Questions
    .intercept(new CreateMaterialQuestion().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const newQuestion = {
            sortId: lesson.material.questions.length,
            question: {
              id: generateUUID(),
              type: input.type,
              question: input.question,
              typeOptions: input.typeOptions,
              validationOptions: input.validationOptions
            }
          };
          lesson.material.questions.push(newQuestion);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateMaterialQuestion().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          const q = lesson.material.questions.find(q => q.question.id === input.id);
          if (q) {
            q.question.type = input.type;
            q.question.question = input.question;
            q.question.typeOptions = input.typeOptions;
            q.question.validationOptions = input.validationOptions;
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new DeleteMaterialQuestion().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.questions = lesson.material.questions.filter(q => q.question.id !== input);
          lesson.material.questions.forEach((q, idx) => q.sortId = idx);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Linked commands
    .intercept(new AddCommand().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          if (!lesson.material.linkedCommands.includes(input)) {
            lesson.material.linkedCommands.push(input);
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new RemoveCommand().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.linkedCommands = lesson.material.linkedCommands.filter(cmdId => cmdId !== input);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    // Minimum number of correct answers
    .intercept(new UpdateLessonMinimumNumberOfCorrectAnswers().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.minimumNumberOfCorrectAnswers = input.minimumNumberOfCorrectAnswers;
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new MoveMaterialRequirements().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.requirements.sort((a, b) => a.sortId - b.sortId); 
          for (const move of input) {
            const index = lesson.material.requirements.findIndex(r => r.requirement.id === move.id);
            if (index === -1) continue;
            handleInPlaceMoveSortOfIdElement(lesson.material.requirements, index, move.moveInstruction.moveDirection);
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new MoveMaterialSteps().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.steps.sort((a, b) => a.sortId - b.sortId);
          for (const move of input) {
            const index = lesson.material.steps.findIndex(r => r.step.id === move.id);
            if (index === -1) continue;
            handleInPlaceMoveSortOfIdElement(lesson.material.steps, index, move.moveInstruction.moveDirection);
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new MoveMaterialQuestions().handle(async (input, ctx) => {
      return saveCourse(getLesson(ctx)
        .maybe(({ course, module, lesson }, meta) => {
          lesson.material.questions.sort((a, b) => a.sortId - b.sortId);
          for (const move of input) {
            const index = lesson.material.questions.findIndex(r => r.question.id === move.id);
            if (index === -1) continue;
            handleInPlaceMoveSortOfIdElement(lesson.material.questions, index, move.moveInstruction.moveDirection);
          }
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
    .intercept(new UpdateCourseState().handle(async (input, ctx) => {
      return saveCourse(getCourse(ctx)
        .maybe((course, meta) => {
          course.state = transitionState(meta, course.state, input.stateTransition);
          return Result.ok(course, meta);
        }), ctx)
        .convertToEmpty();
    }))
}